import { ClockIcon, SparklesIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { Dialog } from '../../../components/UI';
import { IPendingWrapUp } from '../../../services/crm';
import { NotesSection, OutcomeToggle, PersonSection, TasksSection } from './WrapUpSections.component';
import { draftFrom, SENTIMENT, whenLabel, WrapUpDraft } from './wrapup.helpers';

export interface MeetingWrapUpProps {
    item: IPendingWrapUp;
    /** "1 of 3" when more are waiting. */
    position: { index: number; total: number };
    busy: boolean;
    onWrapUp: (draft: WrapUpDraft) => void;
    onLater: () => void;
    onSkip: () => void;
}

// #region MeetingWrapUp
/**
 * One finished call, ready to be wrapped up in under a minute: the summary to
 * glance at, notes and tasks already ticked, an outcome, and one button. The
 * model drafted it; the rep only corrects what is wrong and taps Wrap up.
 */
export const MeetingWrapUp: React.FunctionComponent<MeetingWrapUpProps> = ({ item, position, busy, onWrapUp, onLater, onSkip }): React.ReactElement => {
    const [draft, setDraft] = React.useState<WrapUpDraft>(() => draftFrom(item));
    const [showTranscript, setShowTranscript] = React.useState<boolean>(false);
    React.useEffect(() => { setDraft(draftFrom(item)); setShowTranscript(false); }, [item.bot.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const who = item.lead?.name ?? item.appointment?.invitee_name ?? 'this call';
    const company = item.lead?.company_name;
    const title = item.appointment?.title ?? 'Meeting';
    const when = whenLabel(item.appointment?.start_at ?? item.bot.status_at);
    const sentiment = item.analysis?.sentiment ? SENTIMENT[item.analysis.sentiment] : null;
    const keptTasks = draft.tasks.filter((t) => t.on && t.text.trim()).length;
    const personOk = !draft.person || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.person.email.trim());
    const keptNotes = draft.notes.filter((n) => n.on && n.text.trim()).length;

    return (
        // Clicking outside or pressing Escape does nothing on purpose: the card is only handled through its own buttons.
        <Dialog open={true} onClose={() => undefined} size="2xl" showClose={false} className="!p-0 overflow-hidden">
            {/* Header: soft gradient, the human first, the machine's read as a pill. */}
            <div className="relative bg-gradient-to-br from-emerald-50 via-white to-sky-50 px-7 pb-5 pt-6 dark:from-emerald-950/40 dark:via-zinc-900 dark:to-sky-950/30">
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-700/80 dark:text-emerald-300/80">
                            <SparklesIcon className="h-3.5 w-3.5" /> Meeting wrap-up{position.total > 1 ? <span className="text-zinc-400">· {position.index + 1} of {position.total}</span> : null}
                        </div>
                        <h2 className="mt-1.5 truncate text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">{who}{company ? <span className="font-normal text-zinc-500"> · {company}</span> : null}</h2>
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 text-sm text-zinc-500">
                            <span>{title}</span>{when ? <><span aria-hidden>·</span><span className="inline-flex items-center gap-1"><ClockIcon className="h-3.5 w-3.5" />{when}</span></> : null}
                        </div>
                    </div>
                    {sentiment ? <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium ${sentiment.cls}`}>{sentiment.label}</span> : null}
                </div>
                {item.analysis?.next_step ? (
                    <div className="mt-4 rounded-2xl border border-emerald-500/20 bg-white/70 px-4 py-3 text-sm text-zinc-800 shadow-sm backdrop-blur dark:bg-zinc-900/60 dark:text-zinc-100">
                        <span className="font-medium text-emerald-700 dark:text-emerald-300">Next step · </span>{item.analysis.next_step}
                    </div>
                ) : null}
            </div>

            <div className="max-h-[60vh] overflow-y-auto px-7 py-5">
                <div className="flex flex-col gap-y-6">
                    {draft.person ? (
                        <section>
                            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-400">Who was this with?</h3>
                            <PersonSection person={draft.person} onChange={(person) => setDraft({ ...draft, person })} />
                        </section>
                    ) : null}

                    <section>
                        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-400">Summary</h3>
                        <textarea
                            value={draft.summary} rows={3} onChange={(ev) => setDraft({ ...draft, summary: ev.target.value })}
                            className="w-full resize-none rounded-2xl border border-transparent bg-zinc-500/5 px-4 py-3 text-[15px] leading-7 text-zinc-800 transition focus:border-emerald-500/40 focus:bg-transparent focus:outline-none dark:text-zinc-200"
                        />
                        <button type="button" onClick={() => setShowTranscript((v) => !v)} className="mt-1 text-xs text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200">
                            {showTranscript ? 'Hide what was said' : 'See what was said'}
                        </button>
                        {showTranscript ? <p className="mt-2 whitespace-pre-wrap rounded-2xl bg-zinc-500/5 px-4 py-3 text-sm leading-6 text-zinc-600 dark:text-zinc-300">{item.transcript_excerpt}</p> : null}
                    </section>

                    {draft.notes.length || item.analysis ? (
                        <section>
                            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-zinc-400">Notes for the lead <span className="font-normal text-zinc-400">· {keptNotes} kept</span></h3>
                            <NotesSection notes={draft.notes} onChange={(notes) => setDraft({ ...draft, notes })} />
                        </section>
                    ) : null}

                    <section>
                        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-zinc-400">Tasks <span className="font-normal text-zinc-400">· {keptTasks} will be created</span></h3>
                        <TasksSection tasks={draft.tasks} onChange={(tasks) => setDraft({ ...draft, tasks })} />
                    </section>

                    {item.appointment ? (
                        <section className="flex flex-wrap items-center justify-between gap-3">
                            <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Did it happen?</h3>
                            <OutcomeToggle value={draft.outcome} onChange={(outcome) => setDraft({ ...draft, outcome })} />
                        </section>
                    ) : null}
                </div>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-zinc-500/10 bg-surface-raised px-7 py-4">
                <div className="flex items-center gap-1">
                    <button type="button" onClick={onLater} disabled={busy} className="rounded-full px-3 py-2 text-sm text-zinc-500 transition hover:bg-zinc-500/10 hover:text-zinc-800 dark:hover:text-zinc-200">Later</button>
                    <button type="button" onClick={onSkip} disabled={busy} className="rounded-full px-3 py-2 text-sm text-zinc-400 transition hover:bg-zinc-500/10 hover:text-zinc-700 dark:hover:text-zinc-300">Skip</button>
                </div>
                <button
                    type="button" disabled={busy || !personOk} onClick={() => onWrapUp(draft)}
                    className="inline-flex items-center gap-2 rounded-full bg-zinc-900 px-5 py-2.5 text-sm font-semibold text-white shadow-md transition hover:-translate-y-px hover:shadow-lg active:translate-y-0 disabled:opacity-60 dark:bg-white dark:text-zinc-900"
                >
                    {busy ? 'Wrapping up…' : keptTasks ? `Wrap up · ${keptTasks} task${keptTasks === 1 ? '' : 's'}` : 'Wrap up'}
                </button>
            </div>
        </Dialog>
    );
};
